import {
  COUNTRY_FILTER_ALL,
  COUNTRY_FILTER_FEATURED,
  FEATURED_MARKET_CODES,
  PLATFORM_ACQUISITION_FEE_RATE,
  PLATFORM_ANNUAL_MGMT_FEE_RATE,
  KYC_IDENTITY_BY_COUNTRY,
} from '../config/constants';

const COUNTRIES = window.COOWN_COUNTRIES || {
  US: { name: "United States", code: "US", currency: "USD", symbol: "$", flag: "🇺🇸", rate: 1 },
};

const getCountry = (code) => COUNTRIES[code] || COUNTRIES.US;

/** Apply ISO 4217 currency per country (REST Countries map) before FX merge. */
const applyIsoCountryCurrencies = () => {
  const isoMap = window.COOWN_COUNTRY_CURRENCY;
  const symbols = window.COOWN_CURRENCY_SYMBOLS || {};
  if (!isoMap || typeof isoMap !== "object") return;
  Object.keys(COUNTRIES).forEach((code) => {
    const currency = isoMap[code];
    if (!currency) return;
    const entry = COUNTRIES[code];
    if (!entry) return;
    COUNTRIES[code] = {
      ...entry,
      currency,
      symbol: symbols[currency] || entry.symbol || currency
    };
  });
};
applyIsoCountryCurrencies();

let liveRatesByCurrency = { USD: 1 };
let fxRatesMeta = { updatedAt: null, source: "static", disclaimer: "" };

const mergeLiveFxRates = (ratesByCurrency, meta = {}) => {
  if (ratesByCurrency && typeof ratesByCurrency === "object") {
    liveRatesByCurrency = { USD: 1, ...ratesByCurrency };
  }
  fxRatesMeta = { ...fxRatesMeta, ...meta };
  const ratesLive = meta.source && meta.source !== "static";
  Object.keys(COUNTRIES).forEach((code) => {
    const entry = COUNTRIES[code];
    if (!entry || !entry.currency) return;
    const r = liveRatesByCurrency[entry.currency];
    if (Number.isFinite(r) && r > 0) {
      COUNTRIES[code] = {
        ...entry,
        rate: r,
        hasLocalRate: true,
        rateLive: ratesLive && entry.currency !== "USD"
      };
    }
  });
};

if (window.COOWN_CURRENCY_RATES) {
  Object.entries(window.COOWN_CURRENCY_RATES).forEach(([code, info]) => {
    if (COUNTRIES[code] && Number.isFinite(info.rate)) {
      COUNTRIES[code] = { ...COUNTRIES[code], ...info, hasLocalRate: true };
    }
  });
}

const getDisplayCountry = (code) => {
  if (code === COUNTRY_FILTER_FEATURED) {
    return { name: "Featured markets", code: COUNTRY_FILTER_FEATURED, currency: "USD", symbol: "$", flag: "🌍", rate: 1 };
  }
  if (code === COUNTRY_FILTER_ALL) {
    return { name: "All countries", code: COUNTRY_FILTER_ALL, currency: "USD", symbol: "$", flag: "🌐", rate: 1 };
  }
  const base = getCountry(code);
  const live = liveRatesByCurrency[base.currency];
  if (Number.isFinite(live) && live > 0) {
    return {
      ...base,
      rate: live,
      rateLive: base.currency !== "USD" && Boolean(fxRatesMeta.source && fxRatesMeta.source !== "static")
    };
  }
  return base;
};

const formatUsd = (usdAmount, { maxDecimals = 2, minDecimals } = {}) => {
  const n = Number(usdAmount);
  if (!Number.isFinite(n)) return "$0.00";
  return "$" + n.toLocaleString(undefined, {
    minimumFractionDigits: minDecimals ?? 0,
    maximumFractionDigits: maxDecimals
  });
};

/** Indicative tax on share subtotal — aligned with coown-gcp-backend/src/shared/config/vat.js */
const SUPPORTED_MARKET_TAX = {
  US: { rate: 0, label: "US sales tax", note: "Not added at checkout. Each listing is a real property; you buy fractional ownership shares in the LLC that holds that asset. US sales tax generally does not apply to this type of investment checkout (same as other fractional real estate platforms). Rental income and gains are taxed on your return, not as sales tax here." },
  NG: { rate: 0.075, label: "VAT (7.5%)", note: "Nigeria standard VAT on applicable platform/service charges." },
  GB: { rate: 0.2, label: "VAT (20%)", note: "UK standard rate; exemptions may apply to some financial services." },
  UK: { rate: 0.2, label: "VAT (20%)", note: "UK standard rate; exemptions may apply." },
  DE: { rate: 0.19, label: "VAT (19%)", note: "Germany standard rate." },
  FR: { rate: 0.2, label: "VAT (20%)", note: "France standard rate." },
  IT: { rate: 0.22, label: "VAT (22%)", note: "Italy standard rate." },
  ZA: { rate: 0.15, label: "VAT (15%)", note: "South Africa standard rate." },
  NL: { rate: 0.21, label: "VAT (21%)", note: "Netherlands standard rate." },
  CH: { rate: 0.081, label: "VAT (8.1%)", note: "Switzerland standard rate." }
};

const getSupportedMarketTax = (propertyCountry) => {
  const key = normalizeCountryIso(propertyCountry || "US");
  return SUPPORTED_MARKET_TAX[key] || SUPPORTED_MARKET_TAX.US;
};

const estimateInvestmentTotalUsd = (subtotalUsd, propertyCountry) => {
  const tax = getSupportedMarketTax(propertyCountry);
  const subtotal = Math.round(Number(subtotalUsd) * 100) / 100;
  const acquisitionFeeUsd = Math.round(subtotal * PLATFORM_ACQUISITION_FEE_RATE * 100) / 100;
  const vatAmountUsd = Math.round(subtotal * tax.rate * 100) / 100;
  const totalUsd = Math.round((subtotal + acquisitionFeeUsd + vatAmountUsd) * 100) / 100;
  return {
    subtotalUsd: subtotal,
    acquisitionFeeUsd,
    acquisitionFeeRate: PLATFORM_ACQUISITION_FEE_RATE,
    vatRate: tax.rate,
    vatLabel: tax.label,
    vatNote: tax.note,
    vatCountry: normalizeCountryIso(propertyCountry || "US"),
    vatAmountUsd,
    totalUsd
  };
};

const getInvestmentSubtotalUsd = (investment) => {
  const stored = Number(investment?.subtotalUsd);
  if (Number.isFinite(stored) && stored >= 0) return stored;
  const invested = Number(investment?.invested) || 0;
  const acq = Number(investment?.acquisitionFeeUsd) || 0;
  const vat = Number(investment?.vatAmountUsd) || 0;
  return Math.round(Math.max(0, invested - acq - vat) * 100) / 100;
};

const estimateProRataManagementFeeUsd = (investment, sharesToSell) => {
  const sharesHeld = Number(investment?.shares) || 0;
  if (sharesHeld <= 0 || sharesToSell <= 0) return 0;
  const investedPortion = (getInvestmentSubtotalUsd(investment) / sharesHeld) * sharesToSell;
  const purchaseDate = investment.purchaseDate?.toDate
    ? investment.purchaseDate.toDate()
    : new Date(investment.purchaseDate || Date.now());
  const daysHeld = Math.max(0, (Date.now() - purchaseDate.getTime()) / 86400000);
  return Math.round(investedPortion * PLATFORM_ANNUAL_MGMT_FEE_RATE * (daysHeld / 365) * 100) / 100;
};

const resolveKycCountryCode = (code) => {
  const raw = String(code || "US").toUpperCase();
  if (raw === "UK") return "GB";
  if (raw === COUNTRY_FILTER_FEATURED || raw === COUNTRY_FILTER_ALL) return "US";
  return raw;
};

/** Real ISO code for user profile storage — never persist browse filters (FEATURED / ALL). */
const resolveUserCountryCode = (code, fallback = "US") => {
  const raw = String(code || "").toUpperCase();
  if (!raw || raw === COUNTRY_FILTER_FEATURED || raw === COUNTRY_FILTER_ALL) return fallback;
  if (raw === "UK") return "GB";
  return raw;
};

/** Display label for admin tables; returns null when country was never set. */
const getUserCountryLabel = (code) => {
  const raw = String(code || "").toUpperCase();
  if (!raw || raw === COUNTRY_FILTER_FEATURED || raw === COUNTRY_FILTER_ALL) return null;
  return getCountry(raw).name || raw;
};

const getKycIdentityConfig = (countryCode) => {
  const cc = resolveKycCountryCode(countryCode);
  return KYC_IDENTITY_BY_COUNTRY[cc] || KYC_IDENTITY_BY_COUNTRY.DEFAULT;
};

const maskTaxIdLast4 = (value) => {
  const v = String(value || "").replace(/\D/g, "");
  if (!v) return "—";
  return `***-${v.slice(-4)}`;
};

const getRegionLabel = (code) => getDisplayCountry(code).name;

const getRegionalMarketLabel = (code) => {
  if (code === COUNTRY_FILTER_ALL) return "All Global Markets";
  if (code === COUNTRY_FILTER_FEATURED) return "Featured Markets";
  return getDisplayCountry(code).name;
};

const normalizeCountryIso = (code) => {
  const c = String(code || "").toUpperCase();
  if (c === "UK") return "GB";
  return c;
};

/** Match backend countryFromLocation hints for list-form auto-fill and display sanity. */
const inferCountryFromLocation = (location) => {
  const loc = String(location || "").trim();
  if (!loc) return null;
  const hints = [
    { code: "NG", re: /\bnigeria\b|\blagos\b|\blekki\b|\babuja\b|\bibadan\b/i },
    { code: "GB", re: /\bunited kingdom\b|\b(u\.?k\.?)\b|\bengland\b|\blondon\b/i },
    { code: "DE", re: /\bgermany\b|\bdeutschland\b|\bberlin\b/i },
    { code: "FR", re: /\bfrance\b|\bparis\b/i },
    { code: "IT", re: /\bitaly\b|\brome\b|\bmilan\b/i },
    { code: "ZA", re: /\bsouth africa\b|\bjohannesburg\b|\bcape town\b/i },
    { code: "NL", re: /\bnetherlands\b|\bamsterdam\b/i },
    { code: "CH", re: /\bswitzerland\b|\bzurich\b|\bgeneva\b/i },
    { code: "US", re: /\bunited states\b|\b(u\.?s\.?a?\.?)\b|,\s*(tx|ca|fl|ny|co|wa|ga|il|az|ma|nv|nj|pa|oh|mi)\b/i }
  ];
  for (const { code, re } of hints) {
    if (re.test(loc)) return code;
  }
  return null;
};

const resolveListingCountryClient = (country, location) => {
  const normalized = normalizeCountryIso(country);
  const inferred = inferCountryFromLocation(location);
  if (inferred) {
    if (!normalized || normalized === "US") return inferred;
    if (inferred !== normalized) return inferred;
    return normalized;
  }
  return normalized || "US";
};

/** Infer ISO market code from browse search text (country name, city hints, or code). */
const inferCountryFromSearchQuery = (query) => {
  const q = String(query || "").trim();
  if (!q) return null;
  const fromHints = inferCountryFromLocation(q);
  if (fromHints) return fromHints;
  const lower = q.toLowerCase();
  const entries = Object.entries(COUNTRIES)
    .filter(([code, entry]) => entry?.name && code.length === 2)
    .sort((a, b) => b[1].name.length - a[1].name.length);
  for (const [code, entry] of entries) {
    if (lower.includes(entry.name.toLowerCase())) return normalizeCountryIso(code);
  }
  if (lower.length === 2 && COUNTRIES[lower.toUpperCase()]) return lower.toUpperCase();
  return null;
};

/** When search names a country, use it for market filtering instead of the dropdown alone. */
const getEffectiveBrowseMarket = (selectedCountry, searchQuery) => {
  const fromSearch = inferCountryFromSearchQuery(searchQuery);
  if (fromSearch) return fromSearch;
  return selectedCountry;
};

const propertyMatchesBrowseMarket = (property, selectedCountry, searchQuery) => {
  const market = getEffectiveBrowseMarket(selectedCountry, searchQuery);
  const resolved = resolveListingCountryClient(property?.country, property?.location);
  return propertyMatchesCountry(resolved, market);
};

const isMultiMarketView = (selectedCountry) => {
  const s = (selectedCountry || "").toUpperCase();
  return s === COUNTRY_FILTER_ALL || s === COUNTRY_FILTER_FEATURED;
};

const getCountryFlagImageUrl = (code) => {
  const iso = normalizeCountryIso(code);
  if (!iso || iso.length !== 2 || iso === COUNTRY_FILTER_ALL || iso === COUNTRY_FILTER_FEATURED) return null;
  return `https://flagcdn.com/w40/${iso.toLowerCase()}.png`;
};

/** Dropdown currency column: only codes the live FX table actually supports. */
const getCountryCurrencyLabel = (country) => {
  if (!country?.currency) return null;
  const cur = country.currency;
  const rate = liveRatesByCurrency[cur];
  return Number.isFinite(rate) && rate > 0 ? cur : null;
};

const propertyMatchesCountry = (propertyCountry, selectedCountry) => {
  const p = (propertyCountry || "").toUpperCase();
  const s = (selectedCountry || "").toUpperCase();
  if (!s || s === COUNTRY_FILTER_ALL) return true;
  if (s === COUNTRY_FILTER_FEATURED) {
    return FEATURED_MARKET_CODES.some((code) => {
      const c = code.toUpperCase();
      if (p === c) return true;
      if ((p === "UK" || p === "GB") && (c === "UK" || c === "GB")) return true;
      return false;
    });
  }
  if (p === s) return true;
  if ((p === "UK" || p === "GB") && (s === "UK" || s === "GB")) return true;
  return false;
};

const propertyMatchesSearch = (property, query) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  const title = (property.title || "").toLowerCase();
  const location = (property.location || "").toLowerCase();
  const cityPart = location.split(",")[0].trim().toLowerCase();
  return title.includes(q) || location.includes(q) || cityPart.includes(q);
};

/** Admin Live Listings table: title, location, type, manager, status, id. */
const adminLiveListingMatchesSearch = (property, query) => {
  if (propertyMatchesSearch(property, query)) return true;
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  const pm = propertyManagerFromRecord(property);
  const countryName = property.country ? getCountry(property.country).name : "";
  const haystack = [
    property.type,
    property.status,
    property.country,
    countryName,
    pm.name,
    property.id,
    getPropertyListingMode(property) === "rent" ? "rent rental" : "sale fractional buy shares"
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return haystack.includes(q);
};

const formatMoney = (usdAmount, countryCode) => {
  const c = getDisplayCountry(countryCode);
  const local = usdAmount * c.rate;
  if (local >= 1000000000) return c.symbol + (local / 1000000000).toFixed(1) + "B";
  if (local >= 1000000) return c.symbol + (local / 1000000).toFixed(1) + "M";
  if (local >= 1000) return c.symbol + (local / 1000).toFixed(0) + "K";
  return c.symbol + local.toLocaleString();
};

const formatFull = (usdAmount, countryCode) => {
  const c = getDisplayCountry(countryCode);
  const local = usdAmount * c.rate;
  return c.symbol + local.toLocaleString() + " <span style='color:var(--text-light);font-size:0.85rem'>($" + usdAmount.toLocaleString() + " USD)</span>";
};

/** Property cards, detail, invest flows — full amount (e.g. $450,000), never $0.5M. */
const formatExactMoney = (usdAmount, countryCode, { maxDecimals = 0 } = {}) => {
  const c = getDisplayCountry(countryCode);
  const n = Number(usdAmount);
  if (!Number.isFinite(n)) return c.symbol + "0";
  const local = n * c.rate;
  return c.symbol + local.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals
  });
};

/** Share-level amounts may need cents (e.g. $555.56 per share). */
const formatExactShareMoney = (usdAmount, countryCode) => {
  const c = getDisplayCountry(countryCode);
  const n = Number(usdAmount);
  if (!Number.isFinite(n)) return c.symbol + "0";
  const local = n * c.rate;
  const frac = Math.abs(local - Math.round(local)) > 0.009;
  return c.symbol + local.toLocaleString(undefined, {
    minimumFractionDigits: frac ? 2 : 0,
    maximumFractionDigits: frac ? 2 : 0
  });
};

/** Calculator / summaries: show exact amounts under 1M (e.g. $500), not only "0.0M" */
const formatCalcDisplay = (usdAmount, countryCode) => {
  const c = getDisplayCountry(countryCode);
  const local = Math.round(usdAmount * c.rate);
  if (local >= 1000000) return c.symbol + (local / 1000000).toFixed(2) + "M";
  if (local >= 10000) return c.symbol + (local / 1000).toFixed(1) + "K";
  return c.symbol + local.toLocaleString();
};

/** Portfolio summary cards: use K/M scales instead of forcing millions. */
const formatPortfolioStat = (usdAmount, countryCode) => {
  const c = getDisplayCountry(countryCode);
  const n = Number(usdAmount);
  if (!Number.isFinite(n) || n === 0) return c.symbol + "0";
  const local = n * c.rate;
  if (local >= 1000000) {
    const m = local / 1000000;
    return c.symbol + (m >= 10 ? m.toFixed(0) : m.toFixed(1)) + "M";
  }
  if (local >= 1000) {
    const k = local / 1000;
    if (k >= 100) return c.symbol + Math.round(k) + "K";
    const s = k.toFixed(1).replace(/\.0$/, "");
    return c.symbol + s + "K";
  }
  return formatExactMoney(n, countryCode);
};

/** Navbar balance: $0.00 default; $50.5K after +$500, etc. */
const formatBalanceNav = (usdBalance, countryCode) => {
  const c = getDisplayCountry(countryCode);
  const local = usdBalance * c.rate;
  if (local === 0) return c.symbol + "0.00";
  if (local >= 1000000) return c.symbol + (local / 1000000).toFixed(2) + "M";
  if (local >= 1000) {
    const k = local / 1000;
    if (Number.isInteger(k)) return c.symbol + k + "K";
    const s = k.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
    return c.symbol + s + "K";
  }
  if (local < 1000) return c.symbol + local.toFixed(2);
  return c.symbol + Math.round(local).toLocaleString();
};

const isSpecialCountryFilter = (code) =>
  code === COUNTRY_FILTER_ALL || code === COUNTRY_FILTER_FEATURED;

const getSortedCountryEntries = () =>
  Object.entries(COUNTRIES).sort(([, a], [, b]) => (a.name || "").localeCompare(b.name || ""));

const getPropertyListingMode = (p) => (p?.listingMode === "rent" ? "rent" : "sale");

const propertyManagerFromRecord = (r) => {
  const pm = r?.propertyManager || {};
  const name =
    String(pm.name || "").trim() || String(r?.trustee?.name || "").trim();
  const licenseReference =
    String(pm.licenseReference || "").trim() ||
    String(r?.trustee?.regNumber || "").trim();
  return { name, licenseReference };
};

export {
  COUNTRIES,
  applyIsoCountryCurrencies,
  liveRatesByCurrency,
  fxRatesMeta,
  mergeLiveFxRates,
  getCountry,
  getDisplayCountry,
  formatMoney,
  formatFull,
  formatUsd,
  formatExactMoney,
  formatExactShareMoney,
  formatCalcDisplay,
  formatPortfolioStat,
  formatBalanceNav,
  normalizeCountryIso,
  inferCountryFromLocation,
  inferCountryFromSearchQuery,
  resolveListingCountryClient,
  getEffectiveBrowseMarket,
  propertyMatchesBrowseMarket,
  propertyMatchesCountry,
  propertyMatchesSearch,
  adminLiveListingMatchesSearch,
  getCountryFlagImageUrl,
  getCountryCurrencyLabel,
  isMultiMarketView,
  getRegionLabel,
  getRegionalMarketLabel,
  getSupportedMarketTax,
  SUPPORTED_MARKET_TAX,
  estimateInvestmentTotalUsd,
  estimateProRataManagementFeeUsd,
  resolveKycCountryCode,
  resolveUserCountryCode,
  getUserCountryLabel,
  getKycIdentityConfig,
  maskTaxIdLast4,
  isSpecialCountryFilter,
  getSortedCountryEntries,
  getPropertyListingMode,
  propertyManagerFromRecord,
};
